import { createContext, useContext, useState, useEffect } from "react";
import { db } from "../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import bcrypt from "bcryptjs";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);         // full user object from Firestore
  const [loading, setLoading] = useState(true);   // checking localStorage on mount

  // Restore session from localStorage on app load
  useEffect(() => {
    try {
      const saved = localStorage.getItem("bskr_user");
      if (saved) setUser(JSON.parse(saved));
    } catch {
      localStorage.removeItem("bskr_user");
    }
    setLoading(false);
  }, []);

  // ── LOGIN ──────────────────────────────────────────────────
  async function login(mobile, password) {
    const trimMobile = mobile.trim();

    // Query users collection by mobile
    const q = query(collection(db, "users"), where("mobile", "==", trimMobile));
    const snap = await getDocs(q);

    if (snap.empty) {
      return { success: false, error: "Mobile number not registered." };
    }

    const userDoc = snap.docs[0];
    const data = userDoc.data();
    const uid = userDoc.id;

    // ── Active check — deactivated accounts cannot log in ──
    if (data.active === false) {
      return {
        success: false,
        error: "This account has been deactivated. Please contact your MC.",
      };
    }

    // ── Auto-lock check — ownership transfer 30-day countdown ──
    if (data.autoLockAt) {
      const autoLockDate = data.autoLockAt.toDate
        ? data.autoLockAt.toDate()
        : new Date(data.autoLockAt);
      if (new Date() > autoLockDate) {
        await updateDoc(doc(db, "users", uid), { active: false, autoLockAt: null });
        return {
          success: false,
          error: "This account has been deactivated. Please contact your MC.",
        };
      }
    }

    // ── Lockout check ──────────────────────────────────────
    if (data.lockedUntil) {
      const lockedUntil = data.lockedUntil.toDate
        ? data.lockedUntil.toDate()
        : new Date(data.lockedUntil);
      if (lockedUntil > new Date()) {
        const minsLeft = Math.ceil((lockedUntil - new Date()) / 60000);
        return {
          success: false,
          error: `Account locked. Try again in ${minsLeft} minute${minsLeft > 1 ? "s" : ""}.`,
        };
      }
    }

    // ── Password check ─────────────────────────────────────
    // Support both hashed (bcrypt) and plain-text passwords
    // Plain-text support is for migration — new passwords are always hashed
    let passwordMatch = false;
    if (data.password) {
      if (data.password.startsWith("$2")) {
        // bcrypt hash
        passwordMatch = await bcrypt.compare(password, data.password);
      } else {
        // legacy plain text — match and then upgrade to hash
        passwordMatch = data.password === password;
        if (passwordMatch) {
          const hashed = await bcrypt.hash(password, 10);
          await updateDoc(doc(db, "users", uid), { password: hashed });
        }
      }
    }

    if (!passwordMatch) {
      // Increment failed attempts
      const attempts = (data.failedAttempts || 0) + 1;
      const updateData = { failedAttempts: attempts };

      if (attempts >= 5) {
        // Lock for 15 minutes
        const lockUntil = new Date(Date.now() + 15 * 60 * 1000);
        updateData.lockedUntil = Timestamp.fromDate(lockUntil);
        updateData.failedAttempts = 0; // reset counter after locking
        await updateDoc(doc(db, "users", uid), updateData);
        return {
          success: false,
          error: "Too many failed attempts. Account locked for 15 minutes.",
        };
      }

      await updateDoc(doc(db, "users", uid), updateData);
      const remaining = 5 - attempts;
      return {
        success: false,
        error: `Incorrect password. ${remaining} attempt${remaining > 1 ? "s" : ""} remaining.`,
      };
    }

    // ── Success — reset failed attempts ───────────────────
    await updateDoc(doc(db, "users", uid), {
      failedAttempts: 0,
      lockedUntil: null,
    });

    const sessionUser = {
      uid,
      mobile: data.mobile,
      name: data.name,
      role: data.role,
      flatId: data.flatId || null,
      memberType: data.memberType || "owner",
      paysMaintenance: data.paysMaintenance ?? false,
      paysWater: data.paysWater ?? false,
      societyId: data.societyId || null,
      emailId: data.emailId || data.email || null,
      aadhar: data.aadhar || null,
      entryDate: data.entryDate || null,
      exitDate: data.exitDate || null,
      familyMembers: data.familyMembers || [],
      vehicleNos: data.vehicleNos || [],
      upiIds: data.upiIds || [],
    };

    setUser(sessionUser);
    localStorage.setItem("bskr_user", JSON.stringify(sessionUser));
    return { success: true, user: sessionUser };
  }

  // ── LOGOUT ─────────────────────────────────────────────────
  function logout() {
    setUser(null);
    localStorage.removeItem("bskr_user");
  }

  // ── PASSWORD RESET — Step 1: generate token + save to Firestore ──
  // Called from LoginScreen "Forgot Password" flow.
  // Looks up user by mobile, generates a 32-char hex token,
  // saves it + 1-hour expiry to the users doc, returns email + name
  // so the caller can send the EmailJS email.
  async function generateResetToken(mobile) {
    const trimMobile = mobile.trim();
    const q = query(collection(db, "users"), where("mobile", "==", trimMobile));
    const snap = await getDocs(q);

    if (snap.empty) {
      return { success: false, error: "Mobile number not registered." };
    }

    const userDoc = snap.docs[0];
    const data    = userDoc.data();
    const uid     = userDoc.id;

    if (!data.emailId) {
      return { success: false, error: "No email address found for this account. Please contact your MC." };
    }

    // Generate a secure random 32-char hex token
    const tokenArray = new Uint8Array(16);
    window.crypto.getRandomValues(tokenArray);
    const token = Array.from(tokenArray).map(b => b.toString(16).padStart(2, "0")).join("");

    // 1 hour expiry
    const expiry = new Date(Date.now() + 60 * 60 * 1000);

    await updateDoc(doc(db, "users", uid), {
      resetToken:       token,
      resetTokenExpiry: Timestamp.fromDate(expiry),
    });

    return {
      success:  true,
      token,
      email:    data.emailId,
      name:     data.name || "Resident",
    };
  }

  // ── WATCHMAN PIN RESET — any MC member can reset Watchman's PIN ──
  // Hashes the new 4-digit PIN, updates the Watchman's users doc,
  // clears any lockout, and records who reset it + when (audit trail).
  async function setWatchmanPin(newPin) {
    if (!user || !["President", "Secretary", "Treasurer"].includes(user.role)) {
      return { success: false, error: "Only MC members can reset the Watchman PIN." };
    }
    if (!/^\d{4}$/.test(newPin)) {
      return { success: false, error: "PIN must be exactly 4 digits." };
    }

    const q = query(collection(db, "users"), where("role", "==", "Watchman"));
    const snap = await getDocs(q);

    if (snap.empty) {
      return { success: false, error: "No Watchman account found." };
    }

    const userDoc = snap.docs[0];
    const uid = userDoc.id;
    const hashed = await bcrypt.hash(newPin, 10);

    await updateDoc(doc(db, "users", uid), {
      password: hashed,
      failedAttempts: 0,
      lockedUntil: null,
      pinResetBy: user.name,
      pinResetAt: new Date().toISOString(),
    });

    return { success: true };
  }

  // ── PASSWORD RESET — Step 2: validate token + save new password ──
  // Called from ResetPasswordScreen after resident clicks the email link.
  // Validates token + expiry, hashes new password, saves to Firestore,
  // clears the token fields.
  async function resetPassword(token, newPassword) {
    // Find user with this token
    const q = query(collection(db, "users"), where("resetToken", "==", token));
    const snap = await getDocs(q);

    if (snap.empty) {
      return { success: false, error: "Invalid or expired reset link. Please request a new one." };
    }

    const userDoc = snap.docs[0];
    const data    = userDoc.data();
    const uid     = userDoc.id;

    // Check expiry
    const expiry = data.resetTokenExpiry?.toDate
      ? data.resetTokenExpiry.toDate()
      : new Date(data.resetTokenExpiry);

    if (new Date() > expiry) {
      // Clean up expired token
      await updateDoc(doc(db, "users", uid), {
        resetToken:       null,
        resetTokenExpiry: null,
      });
      return { success: false, error: "Reset link has expired. Please request a new one." };
    }

    // Hash new password and save, clear token
    const hashed = await bcrypt.hash(newPassword, 10);
    await updateDoc(doc(db, "users", uid), {
      password:         hashed,
      resetToken:       null,
      resetTokenExpiry: null,
      failedAttempts:   0,
      lockedUntil:      null,
    });

    return { success: true };
  }

  // ── Role helpers ───────────────────────────────────────────
  const isAdyantam   = () => user?.role === "Adyantam";
  const isPresident  = () => user?.role === "President";
  const isSecretary  = () => user?.role === "Secretary";
  const isTreasurer  = () => user?.role === "Treasurer";
  const isWatchman   = () => user?.role === "Watchman";
  const isMC         = () => ["President", "Secretary", "Treasurer", "Adyantam"].includes(user?.role);
  const isFlatOwner  = () => user?.role === "FlatOwner";
  const isTenant     = () => user?.memberType === "tenant";
  const isFamilyMember = () => user?.memberType === "family";

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        generateResetToken,
        resetPassword,
        isAdyantam,
        isPresident,
        isSecretary,
        isTreasurer,
        isWatchman,
        isMC,
        isFlatOwner,
        isTenant,
        isFamilyMember,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
